// SPDX-FileCopyrightText: 2026 Ebiose AI
// SPDX-License-Identifier: LicenseRef-Ebiose-Proprietary
// All rights reserved. Confidential — unauthorized distribution prohibited.

/**
 * Tolerant parser for the latest-progress `payload_json` string.
 *
 * The wire payload is the verbatim Pydantic `CycleProgressDto`
 * (`ebiose-core/ebiose/logging/events.py`), whose TS shape is the
 * generated `CycleProgressDto` interface in `types/event-journal.d.ts`
 * (pinned by `progress-schema-drift-guard.test.ts`, ADR-0062).
 *
 * Two shapes are observed in the wild, exactly as for snapshots
 * (`_snapshotPayload.ts`):
 *   - Flat: `payload_json` is the verbatim `CycleProgressDto` (what the
 *     Frontend types + fixtures assume — see `event-journal.d.ts`).
 *   - Enveloped: `payload_json` is the full Pydantic `CycleProgressEvent`
 *     payload (message / forge_id / log_level / event_type / n_iteration /
 *     evolution_cycle_id), with the DTO nested under `.progress`. This is
 *     what `ebiose-core`'s `to_wire` actually emits and what the Backend
 *     stores verbatim.
 *
 * Only the flat shape was handled until a live cycle on dev proved the
 * producer never emits it: every field check failed, the parse returned
 * `null`, and `selectAccumulatingTier` silently fell back to the snapshot —
 * so the dashboard only advanced at iteration boundaries, defeating the
 * whole point of the 5 s tick.
 *
 * Returns `null` on parse error or when neither shape carries the
 * recognisable progress shape — the four `Spend` ledgers plus the absolute
 * scalar totals and the phase/emitted-at labels — so a malformed or
 * unexpected payload becomes an absent-data render, never a throw into
 * the dashboard. Mirrors `_snapshotPayload.ts` (ADR-0018: the Backend
 * never deserialises the opaque payload; only the Frontend reads its shape).
 */

/**
 * The additive execution-counter fields the progress payload gained
 * (ProgressContractSeam). An older emitter's payload lacks them, so the seam
 * type marks them optional: reading one yields the number when present and
 * `undefined` when absent — an explicit absence a consumer must resolve, never
 * a fabricated 0 (INV-3 originates here).
 */
type ExecutionField =
  | "n_evaluations"
  | "n_evaluation_errors"
  | "meta_ops_completed"
  | "meta_ops_failed"
  | "n_calls_retried_eval"
  | "n_calls_retried_meta"
  | "n_exhausted"

/**
 * Sanctioned alias for the generated progress wire shape — the dashboard
 * views consume this seam type (never the raw generated `CycleProgressDto`
 * name, pinned by `progressSeamBoundary.test.ts`) so render code stays
 * decoupled from the ambient wire declaration. The execution fields are
 * relaxed to optional so an older payload lacking them stays representable.
 */
export type CycleProgress = Omit<CycleProgressDto, ExecutionField> &
  Partial<Pick<CycleProgressDto, ExecutionField>>

const NUMERIC_TOTALS = [
  "prompt_tokens",
  "completion_tokens",
  "total_llm_calls",
  "total_agents_created",
  "total_agents_evaluated",
  "expected_population_size",
] as const

const STRING_LABELS = ["current_phase", "emitted_at"] as const

const SPEND_LEDGERS = [
  "spend",
  "architect_spend",
  "genetic_spend",
  "evaluation_spend",
] as const

const isObject = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object"

export const parseCycleProgressPayload = (
  payloadJson: string,
): CycleProgress | null => {
  let parsed: unknown
  try {
    parsed = JSON.parse(payloadJson)
  } catch {
    return null
  }
  if (!isObject(parsed)) return null
  // Unwrap the event envelope when present; a flat payload validates as-is.
  const root = isObject(parsed.progress) ? parsed.progress : parsed
  for (const field of NUMERIC_TOTALS) {
    if (typeof root[field] !== "number") return null
  }
  for (const field of STRING_LABELS) {
    if (typeof root[field] !== "string") return null
  }
  for (const ledger of SPEND_LEDGERS) {
    if (!isObject(root[ledger])) return null
  }
  // The execution fields are not required: an older emitter's payload omits
  // them, and the seam type keeps them optional so an absent field reads as
  // `undefined` (explicit absence), never a fabricated 0 (INV-3). Return the
  // envelope-unwrapped `root` (dev's fix) — the execution fields live on the
  // DTO, so a nested `.progress` payload still exposes them.
  return root as unknown as CycleProgress
}
